import { useClickOutside } from "../../../shared/components/Dropdown/useClickOutside";
import type { AdminKpisMultiSelectFilterProps } from "../../models/admin-kpis.models";
import {
  ADMIN_KPIS_FILTER_OPTION_LIST_MAX_HEIGHT,
  ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT,
} from "../../utils/admin-kpis.utils";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { twMerge } from "tailwind-merge";

const AdminKpisMultiSelectFilter = ({
  label,
  options,
  value,
  dataTest,
  disabled = false,
  className,
  onChange,
}: AdminKpisMultiSelectFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeFilterOptions = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
  }, []);

  useClickOutside(closeFilterOptions, containerRef);

  useEffect(() => {
    if (disabled) {
      closeFilterOptions();
    }
  }, [closeFilterOptions, disabled]);

  const selectedLabel = value.length === 0 ? "Choose an option" : `${value.length} selected`;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedSearchTerm) {
      return options;
    }

    return options.filter((option) => option.toLowerCase().includes(normalizedSearchTerm));
  }, [normalizedSearchTerm, options]);
  const selectedValues = useMemo(() => new Set(value), [value]);
  const showClear = value.length > 0 || normalizedSearchTerm.length > 0;
  const optionListHeight = Math.min(
    filteredOptions.length * ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT,
    ADMIN_KPIS_FILTER_OPTION_LIST_MAX_HEIGHT,
  );

  return (
    <div ref={containerRef} className={twMerge("relative min-w-0", className)} data-test={dataTest}>
      <div className="mb-1 text-[11px] font-medium text-[#6B7473] dark:text-gray-300">{label}</div>
      <div className="relative">
        <input
          ref={inputRef}
          type="search"
          value={searchTerm}
          aria-label={`Search ${label}`}
          placeholder={selectedLabel}
          disabled={disabled}
          className="h-9 w-full min-w-0 border border-[#D7E2DE] bg-white px-2 pr-7 text-left text-xs text-gray-900 outline-none transition-colors placeholder:text-[#9AA4A2] hover:border-green focus:border-green disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500"
          onFocus={() => !disabled && setIsOpen(true)}
          onClick={() => !disabled && setIsOpen(true)}
          onChange={(event) => {
            if (disabled) {
              return;
            }

            setSearchTerm(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              closeFilterOptions();
            }
          }}
        />
        <button
          type="button"
          aria-label={`Toggle ${label} filter options`}
          disabled={disabled}
          className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-[#4A5655] transition-colors hover:text-green disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-200"
          onClick={() => {
            if (disabled) {
              return;
            }

            if (isOpen) {
              closeFilterOptions();
              return;
            }

            setIsOpen(true);
            inputRef.current?.focus();
          }}
        >
          <ChevronDownIcon className={twMerge("h-3.5 w-3.5 flex-none transition-transform", isOpen && "rotate-180")} />
        </button>
      </div>
      {isOpen && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden border border-[#D7E2DE] bg-white shadow-md dark:border-gray-700 dark:bg-gray-900">
          {showClear && (
            <button
              type="button"
              className="flex h-8 w-full items-center gap-2 border-b border-[#D7E2DE] px-2 text-left text-xs font-medium text-green hover:bg-[#E9F4F3] dark:border-gray-700 dark:hover:bg-gray-800"
              onClick={() => {
                onChange([]);
                setSearchTerm("");
                inputRef.current?.focus();
              }}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
          {options.length === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-500">No options</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-500">No matching options</div>
          ) : (
            <Virtuoso
              data={filteredOptions}
              style={{ height: optionListHeight }}
              computeItemKey={(_, option) => option}
              fixedItemHeight={ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT}
              itemContent={(_, option) => {
                const isChecked = selectedValues.has(option);
                return (
                  <label
                    className={twMerge(
                      "flex min-h-8 cursor-pointer items-start gap-2 px-2 py-1.5 text-xs hover:bg-[#E9F4F3] dark:hover:bg-gray-800",
                      isChecked && "bg-[#F1FAF8] text-green dark:bg-teal-950/40",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      className="mt-0.5 h-3.5 w-3.5 flex-none accent-green"
                      onChange={(event) => {
                        if (event.target.checked) {
                          onChange([...value, option]);
                          return;
                        }

                        onChange(value.filter((selectedOption) => selectedOption !== option));
                      }}
                    />
                    <span className="break-words">{option}</span>
                  </label>
                );
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default memo(AdminKpisMultiSelectFilter);
